import { z } from "zod";
import {
  EMPLOYMENT_TYPES,
  POSTING_DESCRIPTION_MAX,
  POSTING_TITLE_MAX,
  VENUE_TYPES,
  WORKER_POSITIONS,
  detectContactViolations,
} from "@chotca/config";

export const formSchema = z
  .object({
    venueType: z.enum(VENUE_TYPES),
    // Mirror the canonical WorkerPosition catalog so new F&B roles don't need a
    // second edit here (this used to be a hand-maintained literal union).
    position: z.enum(WORKER_POSITIONS),
    employmentType: z.enum(EMPLOYMENT_TYPES),
    title: z
      .string()
      .min(4, "Tiêu đề tối thiểu 4 ký tự")
      .max(POSTING_TITLE_MAX, `Tiêu đề tối đa ${POSTING_TITLE_MAX} ký tự`)
      .refine((v) => detectContactViolations(v).length === 0, {
        message:
          "Tiêu đề không được chứa số điện thoại, email, mạng xã hội hoặc đường dẫn liên hệ",
      }),
    description: z
      .string()
      .min(10, "Mô tả tối thiểu 10 ký tự")
      .max(
        POSTING_DESCRIPTION_MAX,
        `Mô tả tối đa ${POSTING_DESCRIPTION_MAX} ký tự`,
      )
      .refine((v) => detectContactViolations(v).length === 0, {
        message:
          "Mô tả phát hiện thông tin liên lạc — đăng kèm SĐT/email/mạng xã hội bị cấm và có thể khoá tài khoản vĩnh viễn",
      }),
    // "Thoả thuận" (negotiable pay) is represented by salaryMin === 0 &&
    // salaryMax === 0 — no separate flag. The API + feed already render that as
    // "Thoả thuận" (see isNegotiableSalary). Both amounts allow 0 here so the
    // negotiable case validates; a partial 0 (only one side) is rejected below.
    salaryMin: z.coerce
      .number({ message: "Lương phải là số" })
      .int()
      .nonnegative("Lương tối thiểu không hợp lệ"),
    salaryMax: z.coerce
      .number({ message: "Lương phải là số" })
      .int()
      .nonnegative("Lương tối đa không hợp lệ"),
    salaryType: z.enum(["PER_HOUR", "PER_SHIFT", "PER_DAY", "PER_MONTH"]),
    headcount: z.coerce
      .number({ message: "Số người tuyển phải là số" })
      .int()
      .min(1, "Tuyển ít nhất 1 người")
      .max(50),
    benefits: z.array(z.string()).default([]),
    skillsRequired: z.array(z.string()).default([]),
    skillsPreferred: z.array(z.string()).default([]),
    reqGender: z.enum(["MALE", "FEMALE"]).nullable().optional(),
    reqMinAge: z.coerce.number().int().min(14).max(80).nullable().optional(),
    reqMaxAge: z.coerce.number().int().min(14).max(80).nullable().optional(),
    reqMinHeightCm: z.coerce
      .number()
      .int()
      .min(120)
      .max(220)
      .nullable()
      .optional(),
    reqMotorbike: z.boolean().default(false),
    reqExcludeTattoo: z.boolean().default(false),
    reqKyc: z.boolean().default(false),
    softNotes: z.string().max(500).optional().default(""),
    // Optional square feed-card image. `previewImageKey` is the R2 key sent to
    // the API; `previewImageUrl` is display-only (drives the in-form preview).
    previewImageKey: z.string().nullable().default(null),
    previewImageUrl: z.string().nullable().default(null),
    // Admin-only provenance link (e.g. Facebook permalink). Only rendered when
    // the form mode sets `showSourceUrl` (admin edit). Employers never see it.
    sourceUrl: z.string().nullable().default(null),
    // 1..30 OrgAddress ids attached to this posting. Defaults to empty; the
    // form pre-fills with the org's default address on mount.
    addressIds: z
      .array(z.string().uuid())
      .min(1, "Chọn ít nhất 1 địa điểm làm việc")
      .max(30, "Tối đa 30 địa điểm")
      .default([]),
  })
  .superRefine((v, ctx) => {
    // Both 0 = "Thoả thuận" → allowed, skip amount checks.
    if (v.salaryMin === 0 && v.salaryMax === 0) return;
    // Otherwise a concrete range is required (reject a half-filled 0).
    if (v.salaryMin <= 0) {
      ctx.addIssue({
        code: "custom",
        path: ["salaryMin"],
        message: 'Nhập lương tối thiểu hoặc để trống cả hai để "Thoả thuận"',
      });
    }
    if (v.salaryMax <= 0) {
      ctx.addIssue({
        code: "custom",
        path: ["salaryMax"],
        message: 'Nhập lương tối đa hoặc để trống cả hai để "Thoả thuận"',
      });
    }
    if (v.salaryMin > 0 && v.salaryMax > 0 && v.salaryMin > v.salaryMax) {
      ctx.addIssue({
        code: "custom",
        path: ["salaryMin"],
        message: "Lương tối thiểu phải ≤ lương tối đa",
      });
    }
  })
  .refine(
    (v) =>
      v.reqMinAge == null || v.reqMaxAge == null || v.reqMinAge <= v.reqMaxAge,
    { path: ["reqMinAge"], message: "Tuổi tối thiểu phải ≤ tuổi tối đa" },
  );

export type FormInput = z.input<typeof formSchema>;
export type PostingFormOutput = z.output<typeof formSchema>;

export const DEFAULTS: FormInput = {
  venueType: "RESTAURANT",
  position: "WAITER",
  employmentType: "PART_TIME",
  title: "",
  description: "",
  salaryMin: 0,
  salaryMax: 0,
  salaryType: "PER_MONTH",
  headcount: 1,
  benefits: [],
  skillsRequired: [],
  skillsPreferred: [],
  reqGender: null,
  reqMinAge: null,
  reqMaxAge: null,
  reqMinHeightCm: null,
  reqMotorbike: false,
  reqExcludeTattoo: false,
  reqKyc: false,
  softNotes: "",
  previewImageKey: null,
  previewImageUrl: null,
  sourceUrl: null,
  addressIds: [],
};
